import { Box } from '@mui/material';
import { useContext } from 'react';
import { Loading } from '../../..';
import { vendor as vendorApi } from '../../../../api';
import { VendorBuyingAgreementRebateCategoryDetailContext } from '../../../../contexts/Vendor';
import { useApi, useAuth, useInternationalization } from '../../../../hooks';
import { VendorRebateCategoryRebateEntity } from '../../../../models';
import { VendorRebateCategoryRebateSchema } from '../../../../schemas/Vendor';
import { palette } from '../../../../styles/palette';
import { RebateValueType } from '../../../../types';
import { EditCard } from '../../../Card';
import { LayoutPage } from '../../../Layout';
import {
  VendorRebateCategoryRebateCalculationMethodForm,
  VendorRebateCategoryRebatePaymentTermsForm,
  VendorRebateCategoryRebateValueList,
} from '../../RebateCategoryRebates';

export const VendorBuyingAgreementRebateCategoryRebates = () => {
  const { t } = useInternationalization();
  const { isInternalUser } = useAuth();
  const { vendorBuyingAgreementId, rebateCategory, vendorId, fetchData, readOnly } = useContext(
    VendorBuyingAgreementRebateCategoryDetailContext,
  );
  const {
    data: rebateCategoryRebate,
    isLoading,
    refresh,
  } = useApi(
    vendorApi.getBuyingAgreementRebateCategoryRebate,
    null,
    vendorId,
    vendorBuyingAgreementId,
    rebateCategory.id,
  );
  const { call: update } = useApi(vendorApi.updateBuyingAgreementRebateCategoryRebate, {
    successKey: 'common:success.save',
  });

  const updateRebate = async (entity: VendorRebateCategoryRebateEntity) => {
    return await update(vendorId, vendorBuyingAgreementId, rebateCategory.id, entity);
  };

  const onConfirm = () => {
    refresh();
    fetchData();
  };

  if (isLoading || !rebateCategoryRebate) {
    return <Loading />;
  }

  return (
    <LayoutPage title={t('vendor:rebateCategory.rebates.title')} display="Tab">
      <Box>
        <EditCard
          readOnly={readOnly}
          title={t('vendor:rebateCategory.rebates.sections.calculationMethod')}
          entity={rebateCategoryRebate}
          schema={VendorRebateCategoryRebateSchema('calculationMethod')}
          onSave={updateRebate}
          onConfirm={onConfirm}
        >
          <VendorRebateCategoryRebateCalculationMethodForm />
        </EditCard>
        <EditCard
          readOnly={readOnly}
          title={t('vendor:rebateCategory.rebates.sections.paymentTerms')}
          entity={rebateCategoryRebate}
          schema={VendorRebateCategoryRebateSchema('paymentTerms')}
          onSave={updateRebate}
          onConfirm={onConfirm}
        >
          <VendorRebateCategoryRebatePaymentTermsForm />
        </EditCard>
        <VendorRebateCategoryRebateValueList
          title={t('vendor:rebateCategory.rebates.sections.foidRebates')}
          readOnly={readOnly}
          entity={rebateCategoryRebate}
          onSave={updateRebate}
          onConfirm={onConfirm}
          rebateValueType={RebateValueType.FOID}
          listProperty="foidRebates"
          borderColor={palette.secondary.blue}
        />
        <VendorRebateCategoryRebateValueList
          title={
            isInternalUser
              ? t('vendor:rebateCategory.rebates.sections.receivableRebates')
              : t('vendor:rebateCategory.rebates.sections.rebates')
          }
          readOnly={readOnly}
          entity={rebateCategoryRebate}
          onSave={updateRebate}
          onConfirm={onConfirm}
          rebateValueType={RebateValueType.Receivable}
          listProperty="receivableRebates"
          borderColor={palette.secondary.yellow}
        />
      </Box>
    </LayoutPage>
  );
};
